import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: "parent" | "child";
  escapeKey?: boolean;
  uuid?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  className,
  style,
  variant = "parent",
  uuid = "",
  escapeKey = false,
}) => {
  const [width, setWidth] = useState<string>("60%");
  const [isDragging, setIsDragging] = useState(false);
  const [shouldJiggle, setShouldJiggle] = useState(false);

  const isParent = variant === "parent";
  const initialWidth = isParent ? "60%" : "calc(50% - 60px)";
  const zIndex = isParent ? 50 : 51;

  // Whenever children change, trigger jiggle
  useEffect(() => {
    if (uuid) {
      setShouldJiggle(true);
      const timer = setTimeout(() => {
        setShouldJiggle(false);
      }, 400);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [uuid, isParent]);

  // Set the initial width of the drawer
  useEffect(() => {
    if (open) {
      if (variant === "child" || !width) {
        setWidth(initialWidth);
      }
    }
  }, [open, initialWidth, variant]);

  // Handle dragging to resize the drawer
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const drawerElement = e.currentTarget.parentElement;
    if (!drawerElement) return;

    const currentWidth = drawerElement.getBoundingClientRect().width;
    const startX = e.pageX;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startX - e.pageX;
      const newWidth = Math.max(
        300,
        Math.min(window.innerWidth * 0.8, currentWidth + deltaX)
      );
      setWidth(`${newWidth.toString()}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const computedStyle = {
    ...style,
    width,
    zIndex,
  };

  // Add escape key handling to close the drawer
  useEffect(() => {
    if (escapeKey) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape" && open) {
          onClose();
        }
      };
      window.addEventListener("keydown", handleEscape);
      return () => {
        window.removeEventListener("keydown", handleEscape);
      };
    }
  }, [open, onClose, escapeKey]);

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "20%" }}
      transition={{
        type: "tween",
        duration: 0.2,
        ease: "easeOut",
      }}
      className="fixed inset-0 pointer-events-none"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={twMerge(
            "pointer-events-auto absolute inset-y-0 right-0",
            className,
            isDragging && "select-none"
          )}
          style={computedStyle}
        >
          <div className="relative h-full">
            <motion.div
              animate={
                shouldJiggle
                  ? {
                      x: [0, -3, 0],
                      transition: {
                        duration: 0.3,
                        times: [0, 0.5, 1],
                        ease: "easeOut",
                      },
                    }
                  : {}
              }
              className="h-full"
            >
              <div
                className="h-full bg-chariot-background-primary border-l-2 border-chariot-stroke overflow-y-auto"
                data-testid="drawer-content"
              >
                <button
                  onClick={onClose}
                  className="absolute -left-14 top-6 size-10 rounded-full border-2 border-chariot-stroke bg-chariot-background-primary flex items-center justify-center hover:border-chariot-brand-primary transition-colors"
                >
                  <span className="sr-only">Close panel</span>
                  <X className="size-5 text-chariot-text-primary" />
                </button>

                <div
                  onMouseDown={startResizing}
                  className="absolute -left-10 top-1/2 -translate-y-1/2 h-20 w-6 rounded-full border-2 border-chariot-stroke bg-chariot-background-primary flex items-center justify-center hover:border-chariot-brand-primary transition-colors cursor-ew-resize"
                  role="separator"
                  aria-label="Resize drawer"
                >
                  <div className="flex flex-row gap-1 p-2">
                    <div className="w-0.5 h-6 bg-chariot-text-primary rounded-full" />
                    <div className="w-0.5 h-6 bg-chariot-text-primary rounded-full" />
                  </div>
                </div>

                {children}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface DrawersProps {
  children: React.ReactNode;
  disableBackdrop?: boolean;
}

export const Drawers: React.FC<DrawersProps> = ({ children, disableBackdrop = false }) => {
  const childArray = React.Children.toArray(children);
  const lastTwoDrawers = childArray.slice(-2);
  const parentDrawer = lastTwoDrawers[0];
  const childDrawer = lastTwoDrawers[1];

  const hasParentAndChild =
    React.isValidElement<DrawerProps>(parentDrawer) &&
    React.isValidElement<DrawerProps>(childDrawer) &&
    parentDrawer.props.open &&
    childDrawer.props.open;

  return (
    <>
      {/* Parent Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(parentDrawer) &&
          parentDrawer.props.open && (
            <Drawer
              {...parentDrawer.props}
              key="parent-drawer"
              variant="parent"
              escapeKey={!hasParentAndChild}
              style={{
                ...parentDrawer.props.style,
                width: hasParentAndChild ? "calc(100% - 60px)" : "60%",
              }}
            />
          )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {hasParentAndChild && !disableBackdrop && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              if (React.isValidElement<DrawerProps>(childDrawer)) {
                childDrawer.props.onClose();
              }
            }}
            className="fixed inset-0 bg-chariot-shade/50 pointer-events-auto"
          />
        )}
      </AnimatePresence>

      {/* Child Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(childDrawer) &&
          childDrawer.props.open && (
            <Drawer
              {...childDrawer.props}
              key={`child-drawer-${childDrawer.key ?? "child"}`}
              variant="child"
              escapeKey={true}
              style={{
                width: "calc(50% - 60px)",
                zIndex: 102,
              }}
            />
          )}
      </AnimatePresence>
    </>
  );
};
